import { FC } from 'react';
import { Col, Row, Switch } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentDefinitions } from '@/hooks/useReportComponentDefinitions';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { DataSourceType } from '@/types/api/modules/business-intelligence';

interface AdditionalSettingsProps {
    component: ReportComponent;
}

const AdditionalSettings: FC<AdditionalSettingsProps> = ({ component }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { REPORT_COMPONENT_DEFINITIONS } = useReportComponentDefinitions();

    const updateDraft = useReportComponentStore((s) => s.updateDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft = draftConfigurations[component?.id as string];
    const capabilities = REPORT_COMPONENT_DEFINITIONS[component.type]?.capabilities.additionalSettings;

    const isReady =
        draft?.dataSet?.type && (draft?.dataSet?.key || draft?.dataSet?.type === DataSourceType.THIRD_PARTY);

    const settings = [
        {
            key: 'showZeroValues',
            label: 'Show Zero Values',
            enabled: capabilities?.zeroValues,
            checked: draft?.formatting?.showZeroValues,
        },
        {
            key: 'showAverageLine',
            label: 'Show Average Lines',
            enabled: capabilities?.averageLine,
            checked: draft?.formatting?.showAverageLine,
        },
        {
            key: 'showAxisLabels',
            label: 'Show Axis Labels',
            enabled: capabilities?.axisLabels,
            checked: draft?.formatting?.showAxisLabels,
        },
        {
            key: 'showLegend',
            label: 'Show Legend',
            enabled: capabilities?.legend,
            checked: draft?.formatting?.showLegend,
        },
        {
            key: 'attribution',
            label: 'Show Company Attribution',
            enabled: capabilities?.attribution,
            checked: draft?.formatting?.attribution,
        },
    ].filter((setting) => setting.enabled);

    if (!settings.length) {
        return <></>;
    }

    return (
        <div style={{ padding: '0 1rem' }}>
            <Row gutter={[16, 0]} wrap={true}>
                {settings.map((setting) => (
                    <Col span={6} key={setting.key}>
                        <Form.Item style={{ marginBottom: '0.5rem' }} label={setting.label}>
                            <Switch
                                disabled={!isReady}
                                checked={setting.checked}
                                onChange={(checked) =>
                                    updateDraft(component.id, {
                                        ...(draft as any),
                                        formatting: {
                                            ...(draft?.formatting as any),
                                            [setting.key]: checked,
                                        },
                                    })
                                }
                            />
                        </Form.Item>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default AdditionalSettings;
